(() => {
  const scripts = new Map();
  const styles = new Map();
  const pending = new Map();

  function getAssets() {
    return window.themeAssets || {};
  }

  function loadStyle(key, href) {
    if (!href) {
      return Promise.resolve();
    }
    const cacheKey = key || href;
    if (styles.has(cacheKey)) {
      return styles.get(cacheKey);
    }
    const existing = document.querySelector(`link[data-loader-key="${cacheKey}"]`) || document.querySelector(`link[href="${href}"]`);
    if (existing) {
      const resolved = Promise.resolve(existing);
      styles.set(cacheKey, resolved);
      return resolved;
    }
    const promise = new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.dataset.loaderKey = cacheKey;
      link.onload = () => resolve(link);
      link.onerror = (event) => {
        styles.delete(cacheKey);
        reject(event);
      };
      document.head.appendChild(link);
    });
    styles.set(cacheKey, promise);
    return promise;
  }

  function loadScript(key, src) {
    if (!src) {
      return Promise.resolve();
    }
    const cacheKey = key || src;
    if (scripts.has(cacheKey)) {
      return scripts.get(cacheKey);
    }
    const existing = document.querySelector(`script[data-loader-key="${cacheKey}"]`) || document.querySelector(`script[src="${src}"]`);
    if (existing && typeof existing.dataset.loaderResolved !== "undefined") {
      const resolved = Promise.resolve(existing);
      scripts.set(cacheKey, resolved);
      return resolved;
    }
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = false;
      script.defer = true;
      script.dataset.loaderKey = cacheKey;
      script.onload = () => {
        script.dataset.loaderResolved = "true";
        resolve(script);
      };
      script.onerror = (event) => {
        scripts.delete(cacheKey);
        reject(event);
      };
      document.head.appendChild(script);
    });
    scripts.set(cacheKey, promise);
    return promise;
  }

  function loadSwiper() {
    if (window.Swiper) {
      return Promise.resolve(window.Swiper);
    }
    const assets = getAssets().swiper || {};
    const tasks = [
      loadStyle("swiper-style", assets.css),
      loadScript("swiper-script", assets.js)
    ];
    return Promise.all(tasks).then(() => window.Swiper);
  }

  function loadPhotoSwipe() {
    if (window.PhotoSwipe && window.PhotoSwipeLightbox) {
      return Promise.resolve({
        PhotoSwipe: window.PhotoSwipe,
        PhotoSwipeLightbox: window.PhotoSwipeLightbox
      });
    }
    const assets = getAssets().photoswipe || {};
    const tasks = [
      loadStyle("photoswipe-style", assets.css),
      loadScript("photoswipe-lightbox", assets.lightbox),
      loadScript("photoswipe-core", assets.core)
    ];
    return Promise.all(tasks).then(() => ({
      PhotoSwipe: window.PhotoSwipe,
      PhotoSwipeLightbox: window.PhotoSwipeLightbox
    }));
  }

  function request(library) {
    switch (library) {
      case "swiper":
        return enqueue("swiper", loadSwiper);
      case "photoswipe":
        return enqueue("photoswipe", loadPhotoSwipe);
      default:
        return Promise.resolve();
    }
  }

  function enqueue(key, loader) {
    if (pending.has(key)) {
      return pending.get(key);
    }
    const promise = loader().catch(() => {
      pending.delete(key);
    });
    pending.set(key, promise);
    return promise;
  }

  function whenSwiperReady(callback) {
    return loadSwiper().then((SwiperCtor) => {
      if (typeof callback === "function") {
        callback(SwiperCtor);
      }
      return SwiperCtor;
    });
  }

  function whenPhotoSwipeReady(callback) {
    return loadPhotoSwipe().then((modules) => {
      if (typeof callback === "function") {
        callback(modules);
      }
      return modules;
    });
  }

  const api = {
    loadScript,
    loadStyle,
    loadSwiper,
    loadPhotoSwipe,
    whenSwiperReady,
    whenPhotoSwipeReady,
    request
  };

  api._isStub = false;

  const existing = window.themeLibraryLoader;
  if (existing && existing._isStub && existing.ready && typeof existing.ready.then === "function") {
    api.ready = existing.ready;
  } else {
    api.ready = Promise.resolve(api);
  }

  window.themeLibraryLoader = api;
  window.ShopTheme = window.ShopTheme || {};
  window.ShopTheme.libraryLoader = api;

  if (existing && existing._isStub && typeof existing._flush === "function") {
    existing._flush(api);
  }

  if (!api.ready) {
    api.ready = Promise.resolve(api);
  }
})();
